import React, { useRef, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { ARTWORKS } from '../../data/artworks';
import {
  FOLLOW_DIST, FOLLOW_HEIGHT, AVATAR_SPEED, LOOK_SENS,
  GUESTBOOK_Z, PORTAL_Z, normalizeAngle,
} from './constants';

export default function PlayerController({
  avatarRef, selectedArtwork, onCameraArrived, isDetailMode,
  joystickRef, lookRef, onNearGuestbook, onNearArtworkChange,
  onNearPortal, onExitDetail, isMobile, skipIntro, inputActive = true,
}) {
  const { camera, gl } = useThree();
  const camYaw         = useRef(0);
  const arrivedRef     = useRef(false);
  const introRef       = useRef({ active: !skipIntro, elapsed: 0 });
  const inputActiveRef = useRef(inputActive);
  useEffect(() => { inputActiveRef.current = inputActive; }, [inputActive]);
  const keys           = useRef({});
  const mouseRef       = useRef({ down: false, lastX: 0 });
  const scrollVelocity = useRef(0);
  const lastNearArtworkId = useRef(null);
  const lastNearPortal    = useRef(false);
  const nearGBRef         = useRef(false);
  const SPAWN_Z       = isMobile ? 30 : 24;
  const camPosSmooth  = useRef(new THREE.Vector3(0, FOLLOW_HEIGHT, SPAWN_Z + FOLLOW_DIST));
  const lookAtSmooth  = useRef(new THREE.Vector3(0, 1.1, SPAWN_Z - 6));
  const tempPos       = useRef(new THREE.Vector3());
  const tempLook      = useRef(new THREE.Vector3());

  useEffect(() => { arrivedRef.current = false; }, [selectedArtwork]);

  useEffect(() => {
    const kd = (e) => {
      // Ne pas capturer les touches si un champ de saisie est actif
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      keys.current[e.code] = true;
      if (['KeyW','KeyS','KeyA','KeyD','ArrowUp','ArrowDown','ArrowLeft','ArrowRight'].includes(e.code)) e.preventDefault();
    };
    const ku = (e) => { keys.current[e.code] = false; };
    window.addEventListener('keydown', kd, { passive: false });
    window.addEventListener('keyup', ku);
    return () => { window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku); };
  }, []);

  useEffect(() => {
    const canvas = gl.domElement;
    const down  = (e) => { mouseRef.current.down = true; mouseRef.current.lastX = e.clientX; };
    const up    = () => { mouseRef.current.down = false; };
    const move  = (e) => {
      if (!mouseRef.current.down || isDetailMode || introRef.current.active) return;
      camYaw.current -= (e.clientX - mouseRef.current.lastX) * LOOK_SENS;
      mouseRef.current.lastX = e.clientX;
    };
    const onWheel = (e) => {
      if (isDetailMode || introRef.current.active) return;
      scrollVelocity.current += e.deltaY * 0.004;
      scrollVelocity.current = Math.max(-0.5, Math.min(0.5, scrollVelocity.current));
    };
    canvas.addEventListener('mousedown', down);
    window.addEventListener('mouseup', up);
    window.addEventListener('mousemove', move);
    canvas.addEventListener('wheel', onWheel, { passive: true });
    return () => {
      canvas.removeEventListener('mousedown', down);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('mousemove', move);
      canvas.removeEventListener('wheel', onWheel);
    };
  }, [gl, isDetailMode]);

  useFrame((_, delta) => {
    const av = avatarRef.current;

    // Exit detail mode via any movement (including lateral)
    if (isDetailMode) {
      const jx = joystickRef.current.x, jy = joystickRef.current.y;
      const hasJoystickInput = Math.abs(jx) > 0.15 || Math.abs(jy) > 0.15;
      const hasKeyInput = keys.current['KeyW'] || keys.current['KeyS'] || keys.current['ArrowUp'] || keys.current['ArrowDown']
        || keys.current['KeyA'] || keys.current['KeyD'] || keys.current['ArrowLeft'] || keys.current['ArrowRight'];
      if (hasJoystickInput || hasKeyInput) onExitDetail?.();
    }

    // Exit artwork focus/flight via lateral movement
    if (selectedArtwork && !isDetailMode) {
      const jx = joystickRef.current.x;
      const hasLateralInput = Math.abs(jx) > 0.15
        || keys.current['KeyA'] || keys.current['KeyD']
        || keys.current['ArrowLeft'] || keys.current['ArrowRight'];
      if (hasLateralInput) onExitDetail?.();
    }

    // Intro cinematic
    if (introRef.current.active) {
      introRef.current.elapsed += delta;
      const t     = Math.min(introRef.current.elapsed / 3.2, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      // Intro cinématique : départ relatif au spawn (+8u derrière) → convergence vers position finale
      const introEndZ = av.z + FOLLOW_DIST;
      camera.position.set(0, 10 - eased * (10 - FOLLOW_HEIGHT), introEndZ + 8 - eased * 8);
      camera.lookAt(0, 1.1, av.z - 6 * eased);
      if (t >= 1) {
        introRef.current.active = false;
        camPosSmooth.current.set(0, FOLLOW_HEIGHT, av.z + FOLLOW_DIST);
        lookAtSmooth.current.set(0, 1.1, av.z - 6);
      }
      return;
    }

    // Artwork approach
    if (selectedArtwork && !arrivedRef.current) {
      const tx = (isMobile && selectedArtwork.mobileAvatarTargetX !== undefined)
        ? selectedArtwork.mobileAvatarTargetX
        : selectedArtwork.avatarTargetX;
      const tz = selectedArtwork.avatarTargetZ;
      const ty = selectedArtwork.avatarTargetYaw;
      av.x += (tx - av.x) * 0.055; av.z += (tz - av.z) * 0.055;
      const yd = normalizeAngle(ty - camYaw.current);
      camYaw.current += yd * 0.07; av.yaw = camYaw.current;
      if (Math.abs(tx - av.x) < 0.12 && Math.abs(tz - av.z) < 0.12 && Math.abs(yd) < 0.08) {
        av.x = tx; av.z = tz; av.yaw = ty; camYaw.current = ty;
        arrivedRef.current = true; onCameraArrived?.();
      }
    }

    let foundNear = null;

    // Free movement
    if (!isDetailMode && !selectedArtwork) {
      const speed = delta * AVATAR_SPEED;
      const fwdX  = -Math.sin(camYaw.current);
      const fwdZ  = -Math.cos(camYaw.current);
      if (keys.current['KeyW'] || keys.current['ArrowUp'])    { av.x += fwdX * speed; av.z += fwdZ * speed; }
      if (keys.current['KeyS'] || keys.current['ArrowDown'])  { av.x -= fwdX * speed; av.z -= fwdZ * speed; }
      if (keys.current['KeyA'] || keys.current['ArrowLeft'])  { camYaw.current += delta * 1.8; }
      if (keys.current['KeyD'] || keys.current['ArrowRight']) { camYaw.current -= delta * 1.8; }

      if (Math.abs(scrollVelocity.current) > 0.001) {
        av.x += fwdX * scrollVelocity.current;
        av.z += fwdZ * scrollVelocity.current;
        scrollVelocity.current *= 0.82;
        if (Math.abs(scrollVelocity.current) < 0.001) scrollVelocity.current = 0;
      }

      const jx = joystickRef.current.x, jy = joystickRef.current.y;
      if (Math.abs(jy) > 0.15) { av.x += fwdX * speed * (-jy); av.z += fwdZ * speed * (-jy); }
      if (Math.abs(jx) > 0.15) { camYaw.current -= jx * delta * 2.2; }
      if (lookRef.current.dx !== 0) { camYaw.current -= lookRef.current.dx; lookRef.current.dx = 0; }

      av.yaw = camYaw.current;
      av.x = Math.max(-8.5, Math.min(8.5, av.x));
      av.z = Math.max(-47,  Math.min(isMobile ? 34 : 28,  av.z));

      for (const aw of ARTWORKS) {
        const dx = av.x - aw.avatarTargetX;
        const dz = av.z - aw.avatarTargetZ;
        if (Math.sqrt(dx * dx + dz * dz) < 1.15) { foundNear = aw; break; }
      }
      const newNearId = foundNear ? foundNear.id : null;
      if (newNearId !== lastNearArtworkId.current) {
        lastNearArtworkId.current = newNearId;
        onNearArtworkChange?.(newNearId);
      }

      if (foundNear) {
        const targetYaw = foundNear.wall === 'left' ? Math.PI / 2 : -Math.PI / 2;
        const yd = normalizeAngle(targetYaw - camYaw.current);
        camYaw.current += yd * 0.08;
        const dx = av.x - foundNear.avatarTargetX;
        const dz = av.z - foundNear.avatarTargetZ;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist > 0.05 && dist < 1.15) {
          const pull = 0.038 * (1 - dist / 1.15);
          av.x -= dx * pull;
          av.z -= dz * pull;
        }
      }
    } else {
      if (lastNearArtworkId.current !== null) {
        lastNearArtworkId.current = null;
        onNearArtworkChange?.(null);
      }
    }

    // Portal proximity
    const portalDist = Math.sqrt((av.x) ** 2 + (av.z - PORTAL_Z) ** 2);
    const nowNearPortal = portalDist < 2.2;
    if (nowNearPortal !== lastNearPortal.current) {
      lastNearPortal.current = nowNearPortal;
      onNearPortal?.(nowNearPortal);
    }

    // Guestbook proximity — only call when state CHANGES to avoid 60fps re-renders
    const gbDist    = Math.sqrt((av.x) ** 2 + (av.z - GUESTBOOK_Z) ** 2);
    const nowNearGB = gbDist < 6.5;
    if (nowNearGB !== nearGBRef.current) {
      nearGBRef.current = nowNearGB;
      onNearGuestbook?.(nowNearGB);
    }

    // Camera follow
    const behindX = av.x + Math.sin(camYaw.current) * FOLLOW_DIST;
    const behindZ = av.z + Math.cos(camYaw.current) * FOLLOW_DIST;
    let lx, ly, lz;
    let camPosX = behindX, camPosY = FOLLOW_HEIGHT, camPosZ = behindZ;

    if (selectedArtwork && !arrivedRef.current) {
      // Corrigé : utiliser 9.5 (ROOM_W=19 réel) au lieu de ROOM_W/2=8 (local incorrect)
      lx = selectedArtwork.wall === 'left' ? -9.5 + 0.3 : 9.5 - 0.3;
      ly = 2.5; lz = selectedArtwork.z;
    } else if (foundNear) {
      // ── Vue depuis le cercle — recalibration "juste milieu" ──
      const artFaceX   = foundNear.wall === 'left' ? -9.5 : 9.5;
      const artW       = foundNear.width  || 5.0;
      const artY       = foundNear.yPos   || 5.0;

      // Desktop : légère respiration, œuvre bien présente sans saturer l'écran
      const baseDist   = Math.max(7.8, artW * 1.2  + 1.8);
      // Mobile portrait : distance augmentée de 0.5u pour safe area joystick quand vue "d'en bas"
      const mobileDist = Math.max(12.0, artW * 1.5 + 3.5);
      const viewDist   = isMobile ? mobileDist : baseDist;

      const dir        = foundNear.wall === 'left' ? 1 : -1;
      camPosX = Math.max(-8.5, Math.min(8.5, artFaceX + dir * viewDist));
      camPosZ = foundNear.z;
      // Mobile : caméra très basse (artY-3.4) mais MÊME angle de tilt validé par l'utilisateur (~9.6°)
      // Résultat : l'œuvre se positionne dans la moitié haute de l'écran (28-66% du haut)
      // joystick à ~78-83% du haut → safe area confortable de ~15-20%
      camPosY = isMobile ? Math.max(1.8, artY - 3.4) : FOLLOW_HEIGHT + 1.4;
      lx = artFaceX * 0.985;
      // ly = artY-1.4 maintient le même tilt 9.6° que précédemment validé
      ly = isMobile ? artY - 1.4 : artY;
      lz = foundNear.z;
    } else {
      lx = av.x - Math.sin(camYaw.current) * 6; ly = 1.1; lz = av.z - Math.cos(camYaw.current) * 6;
    }
    tempPos.current.set(camPosX, camPosY, camPosZ);
    tempLook.current.set(lx, ly, lz);
    camPosSmooth.current.lerp(tempPos.current, 0.12);
    lookAtSmooth.current.lerp(tempLook.current, 0.11);
    camera.position.copy(camPosSmooth.current);
    camera.lookAt(lookAtSmooth.current);
  });

  return null;
}
